/**
 * 드롭다운 단일 디자인 원본 — 시스템의 모든 "떠 있는 목록" 이 여기 값만 본다.
 *
 * <h3>왜 파일로 뺐나</h3>
 * 원래 이 스펙은 {@code muiTheme.js} 안의 지역 const 였다. MUI 컴포넌트(Select 메뉴·Autocomplete
 * 팝업)는 styleOverrides 로 자동으로 받으니 그걸로 충분했는데, <b>MUI 가 아닌 팝업</b>이 하나 있다 —
 * 본문 @멘션 자동완성({@code MentionAutocomplete}). 그건 캐럿 위치에 직접 띄우는 Portal 팝퍼라
 * 테마 styleOverrides 가 닿지 않아 자기만의 종이·항목 스타일을 들고 있었고, 결과적으로 시스템에서
 * <b>혼자 다르게 생긴 드롭다운</b>이 됐다(폭 220~320px 고정, 다른 그림자·모서리).
 *
 * <p>그래서 스펙을 이 파일로 올리고 테마와 멘션 팝퍼가 <b>같은 객체</b>를 쓰게 한다.
 * 드롭다운 디자인을 바꿀 일이 생기면 여기만 고치면 둘 다 따라온다.
 *
 * <p>(개별 화면에서 sx·slotProps 로 드롭다운을 꾸미지 말 것 — 여기 값을 바꾼다.)
 *
 * <h3>길이 값은 반드시 문자열 px 로 쓴다</h3>
 * 이 객체는 두 군데로 들어간다 — 테마 {@code styleOverrides}(emotion) 와 컴포넌트 {@code sx}.
 * 두 곳의 <b>숫자 해석이 다르다.</b> emotion 은 {@code paddingTop: 7} 을 7px 로 읽지만, sx 는 같은 값을
 * <b>테마 spacing 배수</b>로 읽어 56px 로 만든다({@code borderRadius} 는 {@code shape.borderRadius} 배수).
 * 숫자로 두면 테마에서는 맞고 sx 에서만 조용히 어긋나 — 화면은 «좀 두꺼운데?» 정도로만 보여 놓치기 쉽다.
 * 문자열 px 는 양쪽 모두 그대로 통과하므로 단위를 붙여 둔다.
 * (같은 함정을 {@code designSystem.js} 의 hiddenScrollbarY 주석도 실측으로 기록해 두었다.)
 */
export const createDropdownSpec = (tokens) => ({
    /** 팝업(종이) — 카드 같은 테두리 + 떠 있는 그림자 + 트리거와 4px 간격. */
    paper: {
        borderRadius: "8px",
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
        backgroundImage: "none",
        marginTop: "4px",
    },

    /**
     * 항목 기본 — 글자 14px, hover 시 primaryBg.
     * whiteSpace/wordBreak: 항목이 길어도 폼필드 너비 안에서 줄바꿈되어 가로로 넘치지 않게 한다.
     */
    item: {
        fontSize: 14,
        borderRadius: "6px",
        margin: "1px 6px",
        minHeight: "auto",
        paddingTop: "7px",
        paddingBottom: "7px",
        color: tokens.colors.textPrimary,
        whiteSpace: "normal",
        wordBreak: "break-word",
        "&:hover": { backgroundColor: tokens.colors.primaryBg },
    },

    /** 선택된 항목 — 연한 파랑 배경 + primary 글자. */
    itemSelected: {
        backgroundColor: tokens.colors.primaryLight,
        color: tokens.colors.primary,
        fontWeight: 600,
    },

    /**
     * 상태 문구("검색 결과가 없습니다" / 로딩) — 고르는 항목이 아니라 안내 줄이다.
     * MUI 기본(AutocompleteNoOptions/Loading)은 padding 만 정하고 fontSize 를 안 정해서
     * 문서 기본 16px 을 물려받는다 — 항목 14px·입력창 13px 사이에서 이 줄만 크게 튄다.
     * 항목보다 한 단계 작게 두어 "고를 수 있는 것" 과 "안내" 를 크기로도 구분한다.
     */
    statusText: {
        fontSize: 13,
        color: tokens.colors.secondary,
        padding: "10px 12px",
    },

    /** 목록(스크롤 영역) 기본 — 항목 margin 과 맞물리는 안쪽 여백. */
    listbox: {
        padding: "4px",
        maxHeight: "320px",
    },
});
